import { useEffect, useRef, useState } from 'react'

/**
 * Motion shared by the chart canvas.
 *
 * Two rules hold everything here together:
 *   - motion is a description of a change that has ALREADY been decided. The
 *     positions, the node set and the drop outcomes are settled first, and the
 *     animation only shows the eye how the chart got there.
 *   - every animation is off under the OS "reduce motion" preference, with the
 *     interaction itself untouched. The CSS side of that is a media query; the
 *     JavaScript side is `prefersReducedMotion`.
 */

/** How long a card that has just left the chart is kept on screen, fading. */
export const CARD_EXIT_MS = 160

/** Share of the remaining distance the loose arrow end covers each frame. */
export const ENDPOINT_EASING = 0.3

/** Below this the loose end is close enough to the pointer and settles on it. */
export const ENDPOINT_SETTLE_PX = 0.5

export interface MotionPoint { x: number; y: number }

/**
 * The OS "reduce motion" preference, read at the moment it matters rather than
 * cached: it can be flipped while the page is open. Environments without
 * `matchMedia` (jsdom, an older browser) simply animate.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** One frame of travel from where the loose end is towards the pointer. */
export function easePoint(current: MotionPoint, target: MotionPoint, factor = ENDPOINT_EASING): MotionPoint {
  const next = {
    x: current.x + (target.x - current.x) * factor,
    y: current.y + (target.y - current.y) * factor,
  }
  const settled = Math.abs(target.x - next.x) <= ENDPOINT_SETTLE_PX &&
    Math.abs(target.y - next.y) <= ENDPOINT_SETTLE_PX
  return settled ? target : next
}

/**
 * Where a dragged point is DRAWN, a little behind where it actually is.
 *
 * The gesture itself — which box the pointer is over, what releasing there
 * means — is always resolved against the real pointer. This is the drawn end of
 * the line only, so the arrow trails the cursor like something being pulled
 * rather than snapping to it frame for frame. The first point of a gesture is
 * taken as-is: a line that eased in from the previous drag's end would flick
 * across the canvas as the drag starts.
 */
export function useEasedPoint(target: MotionPoint | null): MotionPoint | null {
  const [drawn, setDrawn] = useState<MotionPoint | null>(target)
  const drawnRef = useRef<MotionPoint | null>(target)
  const targetRef = useRef<MotionPoint | null>(target)
  const frameRef = useRef(0)
  targetRef.current = target

  useEffect(() => {
    const stop = () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
      frameRef.current = 0
    }

    // The gesture is over, or this is its very first point: nothing to ease
    // between, so the drawn end is simply the real one.
    if (target === null || drawnRef.current === null || prefersReducedMotion()) {
      stop()
      drawnRef.current = target
      setDrawn(target)
      return
    }

    if (frameRef.current) return
    const step = () => {
      frameRef.current = 0
      const goal = targetRef.current
      const from = drawnRef.current
      if (goal === null || from === null) return
      const next = easePoint(from, goal)
      drawnRef.current = next
      setDrawn(next)
      if (next !== goal) frameRef.current = requestAnimationFrame(step)
    }
    frameRef.current = requestAnimationFrame(step)
  }, [target])

  useEffect(() => () => { if (frameRef.current) cancelAnimationFrame(frameRef.current) }, [])

  return drawn
}

interface MotionNode {
  id: string
  data: Record<string, unknown>
  draggable?: boolean
  selectable?: boolean
  selected?: boolean
}

/**
 * Keeps the cards a layout pass has just dropped on the canvas for a moment,
 * marked as leaving, so a collapsed branch fades out instead of vanishing
 * between two frames.
 *
 * A leaving card is a picture: it cannot be picked up, selected or dropped on,
 * it is never part of the layout the drops resolve against, and the next layout
 * pass (or the sweep that scheduled itself) removes it for good.
 */
export function withLeavingNodes<T extends MotionNode>(current: T[], next: T[], animate: boolean): T[] {
  if (!animate) return next.slice()
  const staying = new Set(next.map(node => node.id))
  const leaving = current
    .filter(node => !staying.has(node.id) && !node.data?.leaving)
    .map(node => ({
      ...node,
      draggable: false,
      selectable: false,
      selected: false,
      data: { ...node.data, leaving: true, dropHint: null },
    }))
  return leaving.length === 0 ? next.slice() : [...next, ...leaving]
}

/** Cards that are really on the chart, as opposed to ones fading off it. */
export function withoutLeavingNodes<T extends MotionNode>(nodes: T[]): T[] {
  return nodes.filter(node => !node.data?.leaving)
}
